import * as React from 'react'

import { Box, Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import employeeStatuses from 'modules/core/components/EmployeeMultiFilters/hooks/utils/employeeStatuses'
import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import SearchBar from 'modules/payroll_bundle/components/common/components/_SearchBar'
import getUserByItem from 'modules/payroll_bundle/components/common/helper/getUserByItem'
import type { SupplementType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import type { Actions } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import type Supplement from 'modules/payroll_bundle/store/models/Supplement'

import OptionsBottom from './OptionsBottom'
import { hasEmployeeAssigned, isValid } from './RowValidations'
import Header from './_Header'
import SupplementsGroups from './_SupplementsGroups'
import filterValues from './filterValue'
import { GroupProp } from './types'

interface Props {
  // Supplement actually represents an imported row, i.e. an employee and their supplements
  supplements: Supplement[]
  supplementTypes: SupplementType[]
  onSelectAll?: () => void
  onSelectItem?: (supplement: Supplement) => void
  selectable?: Array<number | null>
  selected?: Set<number>
  checkable?: boolean
  filtersForm: FiltersForm
  onAssignEmployee: (userId: number, index: number) => void
  supplementDispatch: React.Dispatch<Actions>
}

const useSearchFilter = createComputed((search: string, Supplements: Supplement[]) => {
  return Supplements.filter((item) => {
    const user = getUserByItem(item)

    return (
      isEmpty(search) ||
      (item.has('full_name') && matchText(search, item.attributes.full_name)) ||
      (user && matchText(search, user.fullName()))
    )
  })
})

const useGroupSupplementsByErrors = createComputed(
  (
    supplementsToShow: Supplement[],
    supplementTypes: SupplementType[],
    errorRowsMessage: string,
    successfulRowsMessage: string
  ) => {
    const defaultGroupSupplements: GroupProp[] = [
      {
        name: errorRowsMessage,
        count: 0,
        supplements: [],
      },
      {
        name: successfulRowsMessage,
        count: 0,
        supplements: [],
      },
    ]

    return supplementsToShow.reduce((acc: GroupProp[], supplement: Supplement) => {
      const rowIsValid = isValid(supplement, supplementTypes)
      const data = acc.find(
        (item) => item.name === (rowIsValid ? successfulRowsMessage : errorRowsMessage)
      )

      if (!data) {
        throw new Error('Group not found')
      }

      data.count++
      data.supplements.push(supplement)

      return acc
    }, defaultGroupSupplements)
  }
)

const SupplementTable = ({
  checkable,
  selected,
  supplements,
  supplementTypes,
  onSelectAll,
  selectable,
  filtersForm,
  onSelectItem,
  onAssignEmployee,
  supplementDispatch,
}: Props) => {
  const employeeFiltersOptions = {
    skipDefaultFilters: [
      {
        field: 'employee_status',
        value: employeeStatuses.active,
      },
      {
        field: 'employee_status',
        value: employeeStatuses.terminated,
      },
    ],
  }

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const {
    filteredModels: filteredPayrollsupplement,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Supplement>(
    filterValues,
    LOCAL_STORAGE_KEYS.bulkImportSupplementFilter,
    employeeFiltersOptions
  )

  useUnsafeEffect(() => {
    onApplyFilters(supplements)
  }, [supplements]) // eslint-disable-line react-hooks/exhaustive-deps

  const supplementsToShow: Supplement[] = useSearchFilter(
    debouncedSearch,
    filteredPayrollsupplement
  ).get()

  const errorRowsMessage = i18n.t('payroll_bundle.supplements.bulk_import.table.error_rows')
  const successfulRowsMessage = i18n.t(
    'payroll_bundle.supplements.bulk_import.table.successful_row'
  )

  const groupSupplementsToShow = useGroupSupplementsByErrors(
    supplementsToShow,
    supplementTypes,
    errorRowsMessage,
    successfulRowsMessage
  ).get()

  const Options = () => (
    <Box
      width="full"
      alignItems="center"
      flexDirection="row"
      background="grey200"
      justifyContent="spaceBetween"
    >
      <SearchBar
        search={search}
        onSearch={onSearch}
        onCancel={onCancel}
        filters={filters}
        onAddFilter={onAddFilter}
        logicOperator={logicOperator}
        onRemoveFilter={onRemoveFilter}
        onApplyFilters={() => onApplyFilters(supplements)}
      />
    </Box>
  )

  const errorRows = groupSupplementsToShow[0]?.supplements || []
  const supplementTypesWithErrorRows = supplementTypes.reduce(
    (supplementTypeIds, supplementType) => {
      if (
        errorRows.some(
          (s) =>
            hasEmployeeAssigned(s) &&
            !supplementType.legalEntityIdToTaxonomyId[s.attributes.legal_entity_id]
        )
      ) {
        return new Set([...supplementTypeIds, supplementType.id])
      }

      return supplementTypeIds
    },
    new Set<string>()
  )

  return (
    <Table
      autoLayout
      fullWidth
      options={Options}
      optionsBottom={() => (
        <OptionsBottom
          supplements={supplements}
          supplementTypes={supplementTypes}
          selected={selected}
        />
      )}
    >
      <Table noRadius hideBorder>
        <Header
          checkable={checkable}
          supplementTypes={supplementTypes}
          supplementTypesWithErrorRows={supplementTypesWithErrorRows}
          selected={selected}
          selectable={selectable}
          filtersForm={filtersForm}
          onSelectAll={onSelectAll}
          supplementDispatch={supplementDispatch}
        />
      </Table>
      <Table noRadius hideBorder>
        <SupplementsGroups
          checkable={checkable}
          selected={selected}
          onAssignEmployee={onAssignEmployee}
          groups={groupSupplementsToShow}
          supplementTypes={supplementTypes}
          onSelectItem={onSelectItem}
        />
      </Table>
    </Table>
  )
}

export default observer(SupplementTable)
